import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Send, Loader2, AlertTriangle, X, Settings } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const CheckRow = ({ checked, onChange, children, testid }) => (
  <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5 accent-amber-500" data-testid={testid} />
    <span>{children}</span>
  </label>
);

export function EnvoiPrerequisDialog({ open, onClose, prereqId, token, isAdmin, isSuperAdmin, pointsCount, completion, beforeSend, onSent }) {
  const [direction, setDirection] = useState(true);
  const [agenceurs, setAgenceurs] = useState([]);
  const [agSel, setAgSel] = useState([]);
  const [dirEmails, setDirEmails] = useState(null);
  const [extra, setExtra] = useState('');
  const [users, setUsers] = useState([]);
  const [userSel, setUserSel] = useState([]);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/api/users/list`, { headers }).then(r => r.ok ? r.json() : []).then(d => setUsers((d || []).filter(u => u.email))).catch(() => {});
    fetch(`${API}/api/croquis/agenceurs`, { headers }).then(r => r.ok ? r.json() : { items: [] }).then(d => setAgenceurs(d.items || [])).catch(() => {});
    if (isAdmin) fetch(`${API}/api/admin/prerequis-recipients`, { headers }).then(r => r.ok ? r.json() : []).then(d => setDirEmails((d || []).filter(x => x.active !== false).map(x => x.email))).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAg = (id, on) => setAgSel(s => on ? [...s, id] : s.filter(x => x !== id));
  const submit = async () => {
    setBusy(true);
    try {
      await beforeSend();
      const res = await fetch(`${API}/api/prerequis-techniques/${prereqId}/envoyer`, {
        method: 'POST', headers,
        body: JSON.stringify({ send_direction: direction, agenceur_ids: agSel, user_ids: userSel, extra_emails: extra.split(/[,;\s]+/).filter(Boolean), message }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Échec de l'envoi");
      if (d.email_result === 'paused') toast.warning('Fiche soumise — envois emails en pause, rien n\'est parti');
      else toast.success(`Fiche envoyée à ${d.sent_to.join(', ')}`);
      onSent(d);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="prereq-envoi-dialog">
        <DialogHeader><DialogTitle>Envoyer la fiche prérequis</DialogTitle></DialogHeader>
        {(pointsCount > 0 || completion < 100) && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs" data-testid="prereq-envoi-incomplet">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            Dossier rempli à {completion} % ({pointsCount} point(s) à traiter). Vous pouvez l'envoyer quand même : la liste des éléments manquants est jointe au mail.
          </div>
        )}
        <div className="space-y-3">
          <CheckRow checked={direction} onChange={setDirection} testid="envoi-direction">
            <b>Direction</b>{dirEmails ? <span className="text-slate-500"> — {dirEmails.length ? dirEmails.join(', ') : 'aucun email configuré'}</span> : <span className="text-slate-500"> (destinataires configurés)</span>}
          </CheckRow>
          {isSuperAdmin && (
            <a href="/admin?tab=prerequis-recipients" className="inline-flex items-center gap-1 text-xs text-amber-700 underline ml-6" data-testid="envoi-config-direction">
              <Settings className="w-3 h-3" /> Configurer la liste Direction
            </a>
          )}
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 pt-1">Utilisateurs</p>
          <select value="" onChange={(e) => e.target.value && setUserSel(s => [...s, e.target.value])} data-testid="envoi-user-select"
            className="w-full h-9 px-3 rounded-md border border-slate-200 bg-white text-sm">
            <option value="">+ Ajouter un utilisateur…</option>
            {users.filter(u => !userSel.includes(u.id)).map(u => <option key={u.id} value={u.id}>{u.name || u.email} — {u.email}</option>)}
          </select>
          {userSel.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="envoi-user-chips">
              {userSel.map(id => { const u = users.find(x => x.id === id) || {}; return (
                <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-xs text-slate-700">
                  {u.name || u.email}
                  <button type="button" onClick={() => setUserSel(s => s.filter(x => x !== id))} aria-label="Retirer" data-testid={`envoi-user-remove-${id}`}><X className="w-3 h-3" /></button>
                </span>
              ); })}
            </div>
          )}
          {agenceurs.length > 0 && <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 pt-1">Agenceur (optionnel)</p>}
          {agenceurs.map(a => (
            <CheckRow key={a.id} checked={agSel.includes(a.id)} onChange={(on) => toggleAg(a.id, on)} testid={`envoi-ag-${a.id}`}>
              {[a.societe, a.contact].filter(Boolean).join(' — ')} {!a.email && !a.userId && <span className="text-red-600 text-xs">(pas d'email)</span>}
            </CheckRow>
          ))}
          <Input placeholder="Autres emails (séparés par une virgule)" value={extra} onChange={(e) => setExtra(e.target.value)} data-testid="envoi-extra" />
          <Textarea placeholder="Message (optionnel)" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="envoi-message" />
          <p className="text-xs text-slate-500">Le PDF de la fiche est joint au mail. Le créateur de la fiche est mis en copie.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="envoi-cancel">Fermer</Button>
          <Button onClick={submit} disabled={busy || (!direction && !agSel.length && !userSel.length && !extra.trim())} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="envoi-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Envoyer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
